import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import { aboutApi } from "@/api/about";
import { adminApi } from "@/api/admin";
import { authApi, type Impersonator, type SessionUser, type ThemeChoice } from "@/api/auth";

export type SessionState = {
    user: SessionUser | null;
    version: string | null;
};

export const useSessionStore = defineStore("session", {
    state: (): SessionState => ({
        user: null,
        version: null,
    }),
    getters: {
        authenticated: (state) => state.user !== null,
        impersonating: (state): Impersonator | null => state.user?.impersonator ?? null,
        theme: (state): ThemeChoice => (state.user?.preferredTheme === "light" || state.user?.preferredTheme === "dark" ? state.user.preferredTheme : "system"),
    },
    actions: {
        trackUser(): void {
            Sentry.setUser(this.user ? { id: this.user.id, username: this.user.userName } : null);
        },
        async restore(): Promise<boolean> {
            this.user = await authApi.me();
            return this.user !== null;
        },
        async login(email: string, password: string, rememberMe: boolean): Promise<boolean> {
            const ok = await authApi.login(email, password, rememberMe);
            if (ok) this.user = await authApi.me();
            this.trackUser();
            return ok;
        },
        async logout(): Promise<void> {
            await authApi.logout();
            this.user = null;
            this.trackUser();
        },
        async impersonate(id: string): Promise<void> {
            await adminApi.users.impersonate(id);
            this.user = await authApi.me();
            this.trackUser();
        },
        async stopImpersonating(): Promise<string> {
            const stopped = await authApi.stopImpersonating();
            this.user = stopped.signedIn ? await authApi.me() : null;
            this.trackUser();
            return stopped.impersonatedId;
        },
        async setTheme(theme: ThemeChoice): Promise<void> {
            await authApi.setTheme(theme);
            if (this.user) this.user.preferredTheme = theme === "system" ? null : theme;
        },
        async loadVersion(): Promise<void> {
            this.version = (await aboutApi.about()).version;
        },
    },
});
